import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { SophiaAvatar } from '../components/SophiaAvatar';
import { LanguageSelector } from '../components/LanguageSelector';
import { MessageCircle, Sparkles, Sun, Camera, LogOut } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.4 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.85 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 180, damping: 22 },
  },
};

// Each bubble floats at a different rhythm
const floatAnimations = [
  { y: [0, -8, 0, -4, 0], duration: 5 },
  { y: [0, -6, 0, -10, 0], duration: 5.5 },
  { y: [0, -10, 0, -5, 0], duration: 6 },
  { y: [0, -5, 0, -8, 0], duration: 5.8 },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const options = [
    {
      key: 'conversation',
      icon: MessageCircle,
      title: t('home.conversation'),
      desc: t('home.conversationDesc'),
      path: '/conversation',
    },
    {
      key: 'advice',
      icon: Sparkles,
      title: t('home.advice'),
      desc: t('home.adviceDesc'),
      path: '/advice',
    },
    {
      key: 'daily',
      icon: Sun,
      title: t('home.dailyAdvice'),
      desc: t('home.dailyAdviceDesc'),
      path: '/daily',
    },
    {
      key: 'photo',
      icon: Camera,
      title: t('home.photoWithSophia'),
      desc: t('home.photoDesc'),
      path: '/photo',
    },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden noise-overlay">
      {/* Background gradient orbs */}
      <div className="absolute top-[-20%] right-[-10%] w-[400px] h-[400px] bg-sophia-pink-500/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[350px] h-[350px] bg-mystic-teal-500/8 rounded-full blur-3xl pointer-events-none" />

      {/* Language selector and logout */}
      <motion.div
        className="absolute top-6 right-6 z-20 flex items-center gap-2"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
      >
        <LanguageSelector />
        <button
          data-testid="home-logout-btn"
          onClick={handleLogout}
          className="w-9 h-9 rounded-full bg-card/80 backdrop-blur-sm border border-border/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-card transition-colors"
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </motion.div>

      {/* User greeting */}
      {user && (
        <motion.div
          className="absolute top-6 left-6 z-20"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-xs font-body text-muted-foreground">
            {user.name || user.email}
          </p>
        </motion.div>
      )}

      <motion.div
        className="relative z-10 flex flex-col items-center px-6 pt-12 pb-12 min-h-screen"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero: Sophia Avatar */}
        <motion.div variants={itemVariants} className="mb-6">
          <SophiaAvatar variant="pink" size="hero" />
        </motion.div>

        {/* Greeting - ALL CAPS, elegant */}
        <motion.div variants={itemVariants} className="text-center mb-10 space-y-2">
          <h1
            className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-[0.08em] text-foreground uppercase"
            style={{ fontStyle: 'italic' }}
          >
            {t('home.greeting')}
          </h1>
          <p className="font-accent text-base md:text-lg text-foreground/80 max-w-sm mx-auto tracking-wide">
            {t('home.subtitle')}
          </p>
          <p className="font-body text-xs md:text-sm text-muted-foreground tracking-[0.2em] uppercase">
            {t('home.tagline')}
          </p>
        </motion.div>

        {/* Soap bubble feature cards */}
        <div className="grid grid-cols-2 gap-5 w-full max-w-sm">
          {options.map((opt, index) => (
            <motion.button
              key={opt.key}
              data-testid={`home-${opt.key}-btn`}
              variants={itemVariants}
              whileHover={{
                scale: 1.06,
                transition: { type: 'spring', stiffness: 400, damping: 12 },
              }}
              whileTap={{
                scale: 0.92,
                transition: { type: 'spring', stiffness: 400, damping: 12 },
              }}
              onClick={() => navigate(opt.path)}
              className="soap-bubble relative aspect-square rounded-full flex flex-col items-center justify-center gap-2 text-center cursor-pointer group"
            >
              {/* Bubble background layers */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/[0.12] via-sophia-pink-500/[0.06] to-mystic-teal-500/[0.08] backdrop-blur-xl border border-white/[0.15]" />
              
              {/* Iridescent sheen */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-sophia-pink-300/[0.08] via-transparent to-mystic-teal-300/[0.1] group-hover:from-sophia-pink-300/[0.15] group-hover:to-mystic-teal-300/[0.18] transition-all duration-700" />
                <motion.div
                  className="absolute top-[10%] left-[15%] w-[40%] h-[30%] bg-white/[0.08] rounded-full blur-md"
                  animate={{ opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.4 }}
                />
              </div>
              
              {/* Bubble shine highlight */}
              <div className="absolute top-[8%] left-[20%] w-[35%] h-[20%] bg-white/[0.12] rounded-full blur-sm rotate-[-20deg]" />
              
              {/* Float animation wrapper */}
              <motion.div
                className="relative z-10 flex flex-col items-center gap-2 px-3"
                animate={floatAnimations[index]}
                transition={{
                  duration: floatAnimations[index].duration,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <opt.icon className="w-6 h-6 text-foreground/80 group-hover:text-sophia-pink-300 transition-colors duration-300" strokeWidth={1.5} />
                <h3 className="font-display text-sm font-semibold text-foreground leading-tight tracking-wide">
                  {opt.title}
                </h3>
                <p className="font-body text-[10px] text-muted-foreground/80 leading-tight">
                  {opt.desc}
                </p>
              </motion.div>

              {/* Outer glow on hover */}
              <motion.div
                className="absolute inset-[-4px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-sophia-pink-500/[0.12] to-mystic-teal-500/[0.12] blur-md"
              />
            </motion.button>
          ))}
        </div>

        {/* Footer signature */}
        <motion.p
          variants={itemVariants}
          className="mt-14 text-xs text-muted-foreground/40 font-accent tracking-[0.2em] uppercase"
        >
          Sophia Einstein AI
        </motion.p>
      </motion.div>
    </div>
  );
}
